<template>
  <div>
    <Head title="Cobranzas" />

    <app-toolbar 
      name="Cobranza" 
      namePlural="Cobranzas" 
      :hiddenCreate="true" 
      :exportButtons="false"
    >
      <template v-slot:search>
        <search-filter 
          v-model="form.search" 
          class="mr-4 w-full max-w-md" 
          @reset="reset"
        />
      </template>
    </app-toolbar>

    <div class="bg-white rounded-lg shadow-sm overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left bg-gray-50">
            <th class="py-4 px-6 font-semibold w-12"></th>
            <th class="py-4 px-6 font-semibold">Cliente</th>
            <th class="py-4 px-6 font-semibold">Cédula</th>
            <th class="py-4 px-6 font-semibold">Teléfono</th>
            <th class="py-4 px-6 font-semibold">Deuda Total</th>
            <th class="py-4 px-6 font-semibold">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="client in clients.data" :key="client.id">
            <tr class="hover:bg-gray-50 focus-within:bg-gray-50 border-t">
              <td class="px-6 py-4 cursor-pointer text-center" @click="toggleRow(client.id)">
                <i :class="expandedRows.includes(client.id) ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
              </td>
              <td class="px-6 py-4">{{ client.name }}</td>
              <td class="px-6 py-4">{{ client.identification }}</td>
              <td class="px-6 py-4">{{ client.phone }}</td>
              <td class="px-6 py-4 font-bold text-red-600">{{ formatCurrency(client.balance) }}</td>
              <td class="px-6 py-4">
                <button 
                  @click="openAbonoModal(client)" 
                  class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
                >
                  <i class="fas fa-hand-holding-dollar mr-2"></i>Abonar
                </button>
              </td>
            </tr>
            <!-- Expanded row details -->
            <tr v-if="expandedRows.includes(client.id)" class="bg-gray-50">
              <td colspan="6" class="p-6">
                <div class="bg-white rounded shadow p-4 border">
                  <h4 class="font-bold text-lg mb-4 text-gray-700">Facturas Pendientes</h4>
                  <table class="w-full whitespace-nowrap text-sm">
                    <thead>
                      <tr class="text-left border-b">
                        <th class="py-2">Factura</th>
                        <th class="py-2">Fecha</th>
                        <th class="py-2">Vencimiento</th>
                        <th class="py-2">Subtotal</th>
                        <th class="py-2">Pagado</th>
                        <th class="py-2">Deuda</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="order in client.orders" :key="order.id" class="border-b" :class="{'bg-red-50': isOverdue(order.due_date)}">
                        <td class="py-2 text-blue-600 font-medium">#{{ order.id }}</td>
                        <td class="py-2">{{ new Date(order.created_at).toLocaleDateString() }}</td>
                        <td class="py-2" :class="{'text-red-600 font-bold': isOverdue(order.due_date)}">
                          {{ order.due_date ? new Date(order.due_date).toLocaleDateString() : 'N/A' }}
                          <span v-if="order.payment_terms" class="text-xs text-gray-500 block">{{ order.payment_terms }}</span>
                        </td>
                        <td class="py-2">{{ formatCurrency(order.total) }}</td>
                        <td class="py-2">{{ formatCurrency(order.balance) }}</td>
                        <td class="py-2 font-semibold text-red-500">{{ formatCurrency(order.total - order.balance) }}</td>
                      </tr>
                      <tr v-if="!client.orders || client.orders.length === 0">
                        <td colspan="5" class="py-4 text-center text-gray-500">No hay facturas pendientes.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="clients.data.length === 0">
            <td colspan="6" class="px-6 py-8 text-center text-gray-500 border-t">
              No hay clientes con deuda
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <pagination v-if="clients.links" :links="clients.links" class="mt-6" />

    <!-- Abono Modal -->
    <div v-if="showModal" class="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-full max-w-2xl mx-4 overflow-hidden">
        <div class="px-6 py-4 bg-gray-100 border-b flex justify-between items-center">
          <h3 class="text-lg font-bold text-gray-800">
            Abono a: {{ selectedClient?.name }}
          </h3>
          <button @click="closeModal" class="text-gray-500 hover:text-gray-700 focus:outline-none">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <form @submit.prevent="submitAbono" class="p-6">
          <div class="mb-4">
            <label class="block text-gray-700 font-bold mb-2">Seleccione facturas a pagar</label>
            <div class="max-h-48 overflow-y-auto border rounded p-2">
              <div v-for="order in selectedClient?.orders" :key="order.id" class="flex items-center mb-2">
                <input 
                  type="checkbox" 
                  :id="`order-${order.id}`" 
                  :value="order.id" 
                  v-model="abonoForm.orders"
                  class="mr-2"
                >
                <label :for="`order-${order.id}`" class="flex-1 flex justify-between cursor-pointer">
                  <span>Factura #{{ order.id }}</span>
                  <span class="text-red-500 font-semibold">{{ formatCurrency(order.total - order.balance) }}</span>
                </label>
              </div>
              <div v-if="!selectedClient?.orders || selectedClient.orders.length === 0" class="text-gray-500 text-sm">
                No hay facturas pendientes.
              </div>
            </div>
            <div v-if="abonoForm.errors.orders" class="text-red-500 text-xs mt-1">{{ abonoForm.errors.orders }}</div>
          </div>

          <div class="flex flex-wrap -mx-2 mb-4">
            <div class="w-full md:w-1/2 px-2 mb-4 md:mb-0">
              <label class="block text-gray-700 font-bold mb-2">Monto del abono</label>
              <input 
                type="number" 
                step="0.01"
                v-model="abonoForm.amount" 
                class="w-full border rounded px-3 py-2" 
                placeholder="0.00"
                required
              >
              <div v-if="abonoForm.errors.amount" class="text-red-500 text-xs mt-1">{{ abonoForm.errors.amount }}</div>
              
              <div class="mt-2 text-sm text-gray-600">
                Total de la deuda en facturas seleccionadas: <span class="font-bold">{{ formatCurrency(selectedOrdersTotalDebt) }}</span>
              </div>
            </div>
            <div class="w-full md:w-1/2 px-2">
              <label class="block text-gray-700 font-bold mb-2">Método de pago</label>
              <select v-model="abonoForm.method" class="w-full border rounded px-3 py-2" required>
                <option value="" disabled>Seleccione método</option>
                <option v-for="pm in payment_methods" :key="pm.id" :value="pm.name">{{ pm.name }}</option>
              </select>
              <div v-if="abonoForm.errors.method" class="text-red-500 text-xs mt-1">{{ abonoForm.errors.method }}</div>
            </div>
          </div>

          <div class="mb-6">
            <label class="block text-gray-700 font-bold mb-2">Referencia (Opcional)</label>
            <input 
              type="text" 
              v-model="abonoForm.reference" 
              class="w-full border rounded px-3 py-2" 
              placeholder="Ej. Transferencia 12345"
            >
          </div>

          <div class="flex justify-end gap-3">
            <button type="button" @click="closeModal" class="px-4 py-2 border rounded hover:bg-gray-100 transition">
              Cancelar
            </button>
            <button type="submit" :disabled="abonoForm.processing" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50 flex items-center">
              <i v-if="abonoForm.processing" class="fas fa-spinner fa-spin mr-2"></i> Guardar Abono
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import AppToolbar from '@/Shared/AppToolbar.vue'
import SearchFilter from '@/Shared/SearchFilter.vue'
import Pagination from '@/Shared/Pagination.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'

export default {
  name: 'CobranzasIndex',
  components: {
    Head,
    Link,
    AppToolbar,
    SearchFilter,
    Pagination
  },
  layout: Layout,
  props: {
    clients: Object,
    filters: Object,
    payment_methods: Array
  },
  data() {
    return {
      form: {
        search: this.filters.search,
      },
      expandedRows: [],
      showModal: false,
      selectedClient: null,
      abonoForm: this.$inertia.form({
        client_id: '',
        amount: '',
        method: '',
        reference: '',
        orders: []
      })
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get('/cobranzas', { search: this.form.search }, { 
          preserveState: true 
        })
      }, 300),
    },
    'abonoForm.orders': function(newVal) {
      if(this.selectedClient) {
        let total = 0;
        this.selectedClient.orders.forEach(o => {
          if(newVal.includes(o.id)) {
            total += (o.total - o.balance);
          }
        });
        // Opcionalmente pre-llenar el monto con el total
        // this.abonoForm.amount = total;
      }
    }
  },
  computed: {
    selectedOrdersTotalDebt() {
      if(!this.selectedClient || !this.abonoForm.orders.length) return 0;
      let total = 0;
      this.selectedClient.orders.forEach(o => {
        if(this.abonoForm.orders.includes(o.id)) {
          total += (o.total - o.balance);
        }
      });
      return total;
    }
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP'
      }).format(value)
    },
    toggleRow(id) {
      if(this.expandedRows.includes(id)) {
        this.expandedRows = this.expandedRows.filter(rowId => rowId !== id);
      } else {
        this.expandedRows.push(id);
      }
    },
    isOverdue(dueDate) {
      if (!dueDate) return false;
      return new Date(dueDate) < new Date();
    },
    openAbonoModal(client) {
      this.selectedClient = client;
      
      const orderIds = client.orders ? client.orders.map(o => o.id) : [];

      this.abonoForm.reset();
      this.abonoForm.clearErrors();
      this.abonoForm.client_id = client.id;
      this.abonoForm.orders = orderIds;
      
      let total = 0;
      if (client.orders) {
          client.orders.forEach(o => {
              total += (o.total - o.balance);
          });
      }
      this.abonoForm.amount = total;

      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.selectedClient = null;
      this.abonoForm.reset();
    },
    submitAbono() {
      this.abonoForm.post('/cobranzas/abono-masivo', {
        preserveScroll: true,
        onSuccess: () => {
          this.closeModal();
        }
      });
    }
  }
}
</script>
